import React from "react";
import { UNIT_COST_TYPE_OPTIONS } from "./unitDetailHelpers";
import SectionCard from "./SectionCard";

export default function UnitCostsSection({
  recurringUnitCosts,
  oneTimeUnitCosts,
  costForm,
  setCostForm,
  costError,
  costLoading,
  editingCostId,
  unitCostsTotalMonthly,
  onSubmit,
  onEdit,
  onDelete,
  onCancel,
}) {
  return (
    <>
          <div className="xl:col-span-2">
            <SectionCard
              title="Zusätzliche Kosten"
              subtitle="Kostenpositionen aus unit_costs (Monatlich/Jährlich; Einmalig separat)"
            >
              {costError ? (
                <p className="text-sm text-[#f87171] mb-3">{costError}</p>
              ) : null}
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-[#0f172a] dark:text-[#eef2ff]">
                  <thead>
                    <tr className="bg-slate-100 dark:bg-[#111520] text-[9px] font-bold uppercase tracking-[.8px] text-[#64748b] dark:text-[#6b7a9a]">
                      <th className="py-2 pr-4 font-medium">Kostenart</th>
                      <th className="py-2 pr-4 font-medium">Betrag (CHF)</th>
                      <th className="py-2 pr-4 font-medium">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recurringUnitCosts.length === 0 ? (
                      <tr>
                        <td
                          colSpan={3}
                          className="py-3 text-[#64748b] dark:text-[#6b7a9a] text-sm"
                        >
                          {costLoading && !costError
                            ? "Lade …"
                            : "Keine laufenden Kosten erfasst."}
                        </td>
                      </tr>
                    ) : (
                      recurringUnitCosts.map((row) => (
                        <tr
                          key={String(row.id)}
                          className="border-b border-black/10 dark:border-white/[0.05]"
                        >
                          <td className="py-2 pr-4 font-medium">
                            {row.cost_type || "—"}
                          </td>
                          <td className="py-2 pr-4">
                            {Number.isFinite(Number(row.amount_chf))
                              ? `CHF ${Number(row.amount_chf).toLocaleString("de-CH", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                              : "—"}
                            <span className="ml-2 text-xs text-[#64748b] dark:text-[#6b7a9a]">
                              {String(row.frequency || "monthly").trim().toLowerCase() === "yearly"
                                ? "(jährlich)"
                                : "(monatlich)"}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            <div className="flex flex-wrap items-center gap-3">
                              <button
                                type="button"
                                disabled={costLoading}
                                onClick={() => onEdit(row)}
                                className="text-sky-700 hover:underline text-sm font-medium dark:text-sky-400 disabled:opacity-50"
                              >
                                Bearbeiten
                              </button>
                              <button
                                type="button"
                                disabled={costLoading}
                                onClick={() => onDelete(row)}
                                className="text-[#f87171] hover:underline text-sm font-medium disabled:opacity-50"
                              >
                                Löschen
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-[#0f172a] dark:text-[#eef2ff] mt-3 font-medium">
                Total: CHF{" "}
                {unitCostsTotalMonthly.toLocaleString("de-CH", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                / Monat
              </p>
              <form
                onSubmit={onSubmit}
                className="mt-6 pt-4 border-t border-black/10 dark:border-white/[0.07] space-y-3"
              >
                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3">
                  <label className="flex flex-col gap-1 text-sm text-[#6b7a9a]">
                    <span>Kostenart</span>
                    <select
                      value={costForm.cost_type}
                      onChange={(e) =>
                        setCostForm((f) => ({
                          ...f,
                          cost_type: e.target.value,
                          custom_type:
                            e.target.value === "Sonstiges" ? f.custom_type : "",
                        }))
                      }
                      disabled={costLoading}
                      className="text-sm border border-black/10 dark:border-white/[0.08] rounded-[8px] px-2 py-1.5 bg-slate-100 dark:bg-[#111520] text-[#0f172a] dark:text-[#eef2ff] disabled:opacity-50 min-w-[200px]"
                    >
                      <option value="">— wählen —</option>
                      {UNIT_COST_TYPE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </label>
                  {costForm.cost_type === "Sonstiges" ? (
                    <label className="flex flex-col gap-1 text-sm text-[#6b7a9a]">
                      <span>Bezeichnung</span>
                      <input
                        type="text"
                        value={costForm.custom_type}
                        onChange={(e) =>
                          setCostForm((f) => ({
                            ...f,
                            custom_type: e.target.value,
                          }))
                        }
                        disabled={costLoading}
                        className="text-sm border border-black/10 dark:border-white/[0.08] rounded-[8px] px-2 py-1.5 bg-slate-100 dark:bg-[#111520] text-[#0f172a] dark:text-[#eef2ff] disabled:opacity-50 min-w-[200px]"
                        placeholder="z. B. Haftpflicht"
                      />
                    </label>
                  ) : null}
                  <label className="flex flex-col gap-1 text-sm text-[#6b7a9a]">
                    <span>Betrag (CHF)</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={costForm.amount_chf}
                      onChange={(e) =>
                        setCostForm((f) => ({
                          ...f,
                          amount_chf: e.target.value,
                        }))
                      }
                      disabled={costLoading}
                      className="text-sm border border-black/10 dark:border-white/[0.08] rounded-[8px] px-2 py-1.5 bg-slate-100 dark:bg-[#111520] text-[#0f172a] dark:text-[#eef2ff] disabled:opacity-50 w-40"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-[#6b7a9a]">
                    <span>Frequenz</span>
                    <select
                      value={costForm.frequency || "monthly"}
                      onChange={(e) =>
                        setCostForm((f) => ({ ...f, frequency: e.target.value }))
                      }
                      disabled={costLoading}
                      className="text-sm border border-black/10 dark:border-white/[0.08] rounded-[8px] px-2 py-1.5 bg-slate-100 dark:bg-[#111520] text-[#0f172a] dark:text-[#eef2ff] disabled:opacity-50 min-w-[160px]"
                    >
                      <option value="monthly">Monatlich</option>
                      <option value="yearly">Jährlich</option>
                      <option value="one_time">Einmalig</option>
                    </select>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="submit"
                      disabled={costLoading}
                      className="text-sm bg-gradient-to-r from-[#5b8cff] to-[#7c5cfc] text-white font-semibold rounded-[8px] border-none px-4 py-2 disabled:opacity-50"
                    >
                      {costLoading ? "…" : "Speichern"}
                    </button>
                    <button
                      type="button"
                      disabled={costLoading}
                      onClick={onCancel}
                      className="text-sm border border-black/10 dark:border-white/[0.1] hover:bg-slate-100 dark:hover:bg-[#111520] text-[#0f172a] dark:text-[#eef2ff] px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                    >
                      Abbrechen
                    </button>
                  </div>
                </div>
                {editingCostId ? (
                  <p className="text-xs text-[#6b7a9a]">
                    Bearbeitung: Eintrag wird aktualisiert.
                  </p>
                ) : null}
              </form>
            </SectionCard>
          </div>

          <div className="xl:col-span-2">
            <SectionCard
              title="Einmalige Kosten"
              subtitle="Einmalige Buchungen; nicht in den laufenden Kosten oder den KPI oben enthalten"
            >
              {oneTimeUnitCosts.length === 0 ? (
                <p className="text-[10px] text-[#64748b] dark:text-[#6b7a9a]">Keine einmaligen Kosten erfasst.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-[#0f172a] dark:text-[#eef2ff]">
                    <thead>
                      <tr className="bg-slate-100 dark:bg-[#111520] text-[9px] font-bold uppercase tracking-[.8px] text-[#64748b] dark:text-[#6b7a9a]">
                        <th className="py-2 pr-4 font-medium">Kostenart</th>
                        <th className="py-2 pr-4 font-medium">Betrag CHF</th>
                        <th className="py-2 pr-4 font-medium">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {oneTimeUnitCosts.map((row) => (
                        <tr
                          key={String(row.id)}
                          className="border-b border-black/10 dark:border-white/[0.05]"
                        >
                          <td className="py-2 pr-4 font-medium">
                            {row.cost_type || "—"}
                          </td>
                          <td className="py-2 pr-4">
                            {Number.isFinite(Number(row.amount_chf))
                              ? `CHF ${Number(row.amount_chf).toLocaleString("de-CH", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`
                              : "—"}
                          </td>
                          <td className="py-2 pr-4">
                            <button
                              type="button"
                              disabled={costLoading}
                              onClick={() => onDelete(row)}
                              className="text-[#f87171] hover:underline text-sm font-medium disabled:opacity-50"
                            >
                              Löschen
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </div>
    </>
  );
}
